import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/base/Modal";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { activityErrorMessage, fetchActivities, updateActivity, type ActivityRow } from "../services/activitiesService";
import { STATUS_TONE } from "../lib/format";

// ---------------------------------------------------------------------------
// «Asignar tickets» a una épica desde el Roadmap: lista los tickets abiertos
// que aún no cuelgan de ella (backlog y sprints), con buscador y casillas, y
// les pone `parentId`. Nunca mueve lo que ya es de otra épica sin que se vea:
// esos salen con la clave de su épica actual.
// ---------------------------------------------------------------------------

interface Props {
  epic: ActivityRow;
  onClose: () => void;
  onAssigned: (count: number) => void;
}

export default function AssignTicketsModal({ epic, onClose, onAssigned }: Props) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const [rows, setRows] = useState<ActivityRow[] | null>(null);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchActivities({ includeDone: false, limit: 2000 })
      .then((res) => alive && setRows(res.data.filter((a) => !a.archived && !a.isEpic && a.id !== epic.id && a.parentId !== epic.id)))
      .catch((err) => alive && setError(activityErrorMessage(err) ?? t("staffActivities.common.error")));
    return () => {
      alive = false;
    };
  }, [epic.id, t]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows ?? []).filter((a) => !q || a.title.toLowerCase().includes(q) || a.key.toLowerCase().includes(q));
  }, [rows, search]);

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    if (!picked.size) return;
    setSaving(true);
    setError(null);
    try {
      await Promise.all([...picked].map((id) => updateActivity(id, { parentId: epic.id })));
      onAssigned(picked.size);
    } catch (err) {
      setSaving(false);
      setError(activityErrorMessage(err) ?? t("staffActivities.common.error"));
    }
  };

  const field = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${tc.border} ${tc.cardBg} ${tc.text}`;

  return (
    <Modal
      title={t("staffActivities.epic.assignTitle", { epic: epic.title })}
      themeClasses={tc}
      onClose={() => !saving && onClose()}
      width="max-w-2xl"
      content={
        <div className="space-y-3" data-testid="assign-tickets">
          <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.epic.assignHint")}</p>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("staffActivities.backlog.search")} className={field} aria-label={t("staffActivities.backlog.search")} />
          {rows === null && !error && <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.common.loading")}</p>}
          {rows !== null && visible.length === 0 && <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.epic.assignEmpty")}</p>}
          {visible.length > 0 && (
            <ul className={`max-h-80 divide-y overflow-y-auto rounded-xl border ${tc.cardBorder}`}>
              {visible.map((a) => (
                <li key={a.id}>
                  <label className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm ${tc.text} ${tc.buttonHover}`}>
                    <input type="checkbox" checked={picked.has(a.id)} onChange={() => toggle(a.id)} className="h-4 w-4 accent-violet-500" data-testid={`assign-pick-${a.id}`} />
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATUS_TONE[a.status]}`}>{t(`staffActivities.status.${a.status}`)}</span>
                    <span className={`font-mono text-[11px] ${tc.textSecondary}`}>{a.key}</span>
                    <span className="flex-1 truncate">{a.title}</span>
                    {a.parentId && <span className={`text-[11px] italic ${tc.textSecondary}`}>{t("staffActivities.epic.hasOtherEpic")}</span>}
                  </label>
                </li>
              ))}
            </ul>
          )}
          {error && (
            <p className="text-xs text-red-500" data-testid="assign-error">
              {error}
            </p>
          )}
          <div className="flex items-center justify-between gap-2">
            <span className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.backlog.selected", { count: picked.size })}</span>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} disabled={saving} className={`rounded-lg border px-3 py-2 text-xs ${tc.border} ${tc.textSecondary}`}>
                {t("staffActivities.common.cancel")}
              </button>
              <button type="button" onClick={() => void save()} disabled={saving || picked.size === 0} className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-60" data-testid="assign-save">
                {saving ? t("staffActivities.epic.assigning") : t("staffActivities.epic.assign")}
              </button>
            </div>
          </div>
        </div>
      }
    />
  );
}
